import { genId } from '@/scripts/utils';
import type { ChatArgType, ChatChatId } from '../useChats/types';
import type { ChatMessageKey, ChatMessageMessageIdQuery } from './types';
import {
  ChatMessageCategory,
  ChatMessageDeleted,
  ChatMessageMentioned,
  ChatMessagePayloadType,
  ChatMessagePulled,
  ChatMessageStatus,
  type ChatMessage,
  type ChatMessagePartial,
  type ChatMessageQuery,
  type ChatMessagesItem,
  type ChatMessagesMap,
  type DBChatMessage,
  type DBChatMessagePartial,
} from './types';

export function genDBChatMessage(message: DBChatMessagePartial): DBChatMessage {
  const cli_msg_id = message.cli_msg_id || genId();
  return {
    id: 0,
    category: ChatMessageCategory.SEND,
    status: ChatMessageStatus.LOADING,
    message_id: '',
    payload_type: ChatMessagePayloadType.TEXT,
    payload: '',
    owner_id: '',
    link_msg_id: '',
    attach: 0,
    edit: 0,
    pin: 0,
    sticky: 0,
    deleted: ChatMessageDeleted.NO,
    pulled: ChatMessagePulled.NO,
    read_at: 0,
    create_at: 0,
    update_at: 0,
    expire_duration: 0,
    at_list: '',
    at_flag: ChatMessageMentioned.NO,
    ...message,
    cli_msg_id,
  };
}

export function genDBChatMessages(messages: DBChatMessagePartial[]) {
  return messages.map(genDBChatMessage);
}

export function genChatMessage(message: ChatMessagePartial): ChatMessage {
  const dbMessage = genDBChatMessage(message);
  return {
    encrypt_index: 0,
    ...dbMessage,
    ...message,
  };
}

export function genChatMessages(messages: ChatMessagePartial[]) {
  return messages.map(genChatMessage);
}

export function getChatMessagesByMap(map: ChatMessagesMap, id: ChatChatId) {
  const chatMessagesItem: ChatMessagesItem | undefined = map[id];

  function getInfo(chatMessageQuery: ChatMessageQuery) {
    const map = chatMessagesItem?.messagesInfoMap;
    if (map) {
      return (
        map[chatMessageQuery.message_id!] ||
        map[chatMessageQuery.id!] ||
        map[chatMessageQuery.cli_msg_id!]
      );
    }
  }

  function get(chatMessageQuery: ChatMessageQuery) {
    return getInfo(chatMessageQuery)?.message;
  }

  return {
    messages: chatMessagesItem?.messages || [],
    getInfo,
    get,
  };
}

export type GetChatMessagesUtil = ReturnType<typeof getChatMessagesByMap>;

const statusMap = {
  '@': {
    status: ChatMessageStatus.UNREAD,
  },
  '*': {
    status: ChatMessageStatus.READ,
  },
  '-': {}, // TODO amo
} as MyRecord<ChatMessagePartial>;

const regex = new RegExp(
  `^(?:(\\d+?)_)?([${Object.keys(statusMap).join('')}])?(\\S+?)(?:_(\\S*))?$`
);

export function parseMessageId(str: string) {
  const msg: ChatMessageMessageIdQuery & ChatMessagePartial = {
    message_id: str,
  };

  const match = str.match(regex);

  if (match) {
    const [, payload_type, prefix, message_id, cli_msg_id] = match;

    msg.message_id = message_id;

    if (payload_type) {
      msg.payload_type = Number(payload_type);
    }

    if (prefix) {
      Object.assign(msg, statusMap[prefix]);
    }

    if (cli_msg_id && !cli_msg_id.startsWith('!')) {
      msg.cli_msg_id = cli_msg_id;
    }

    msg.messageId = prefix ? prefix + message_id : message_id;
  }

  return msg;
}

export function mergeMessage<
  T extends ChatMessagePartial,
  U extends ChatMessagePartial,
>(map: Map<T, T & U>, msg: T, msg1: U) {
  const msg_ = map.get(msg) || msg;
  const { status: status1, read_at: read_at1, ...rest } = msg1;

  const result = {
    ...msg_,
    ...rest,
  } as T & U;

  if (status1 !== undefined) {
    if (
      status1 !== ChatMessageStatus.UNREAD ||
      msg_.status !== ChatMessageStatus.READ
    ) {
      result.status = status1;
    }
  }

  if (read_at1 !== undefined) {
    if (!msg_.read_at) {
      result.read_at = read_at1;
    }
  }

  map.set(msg, result);

  return result;
}

export function pushMessagesArgs<
  T extends ChatArgType<U>,
  U extends ChatMessagePartial = ChatMessagePartial,
>(args: T[] = []) {
  const map: Record<string, T> = {};

  function push(msg: U, chat_id: string) {
    let arg_ = map[chat_id];
    if (!arg_) {
      arg_ = map[chat_id] = { chat_id, messages: [] } as unknown as T;
      args.push(arg_);
    }
    arg_.messages.push(msg);
  }

  return {
    args,
    push,
  };
}

export async function eachMessagesArgs<
  T extends ChatArgType<U>,
  U extends ChatMessagePartial = ChatMessagePartial,
>(args: T[], cb: (msg: U, chat_id: T['chat_id']) => any) {
  for (const arg of args) {
    const { chat_id, messages } = arg;
    for (const msg of messages) {
      await cb(msg, chat_id);
    }
  }
}

export async function filterMessagesArgs<
  T extends ChatArgType<U>,
  U extends ChatMessagePartial = ChatMessagePartial,
>(args: T[], cb: (msg: U, chat_id: T['chat_id']) => any) {
  const { args: args_, push } = pushMessagesArgs<T, U>();
  for (const arg of args) {
    const { chat_id, messages } = arg;
    for (const msg of messages) {
      if (await cb(msg, chat_id)) {
        push(msg, chat_id);
      }
    }
  }
  return args_;
}

export async function mapMessagesArgs<T extends ChatArgType<any>, R = any>(
  args: T[],
  cb: (
    msg: T['messages'][number],
    chat_id: T['chat_id'],
    index: number
  ) => ResolveValue<R>
) {
  await Promise.all(
    args.map(async (arg) => {
      const { chat_id, messages } = arg;
      arg.messages = await Promise.all(
        messages.map(async (msg, index) => await cb(msg, chat_id, index))
      );
    })
  );
  return args as Array<ChatArgType<R>>;
}

// TODO amo: Optimize
export function mergeMessagesArgs<
  T extends ChatArgType<U>,
  U extends ChatMessagePartial = ChatMessagePartial,
>(args: T[], args1: T[]) {
  const map = args.reduce(
    (map, arg) => {
      map[arg.chat_id] = arg;
      for (const msg of arg.messages) {
        if (msg.id) {
          map[msg.id] = msg;
        }
        if (msg.message_id) {
          map[msg.message_id] = msg;
        }
        if (msg.cli_msg_id) {
          map[msg.cli_msg_id] = msg;
        }
      }
      return map;
    },
    {} as Record<ChatMessageKey & ChatChatId, U | T>
  );

  for (const arg1 of args1) {
    const { chat_id, messages } = arg1;
    const arg = map[chat_id] as T;
    if (arg) {
      for (const msg1 of messages) {
        const msg = map[msg1.id!] || map[msg1.message_id!] || map[msg1.cli_msg_id!];
        if (msg) {
          Object.assign(msg, msg1);
        } else {
          arg.messages.push(msg1);
        }
      }
    } else {
      args.push(arg1);
    }
  }

  return args;
}

export function flatMessagesArgs<
  T extends ChatArgType<U>,
  U extends ChatMessagePartial = ChatMessagePartial,
>(args: T[]) {
  return args.map<U[]>((item) => item.messages).flat();
}

export function isMediaMessage(message: ChatMessagePartial | undefined) {
  return (message?.payload_type &&
    ChatMessagePayloadType.MEDIA_AUDIO_CALL <= message.payload_type &&
    message.payload_type <= ChatMessagePayloadType.MEDIA_VIDEO_CALL) as boolean;
}

export function isDeleteMessage(message: ChatMessagePartial | undefined) {
  return (message?.payload_type &&
    ChatMessagePayloadType.DELETE_MESSAGE === message.payload_type) as boolean;
}

export function isGroupUpdateSystemMessage(message: ChatMessagePartial | undefined) {
  return (message?.payload_type &&
    ((ChatMessagePayloadType.GROUP_CREATE <= message.payload_type &&
      message.payload_type <= ChatMessagePayloadType.GROUP_MEMBER_QUIT) ||
      message.payload_type === ChatMessagePayloadType.GROUP_UPDATE_INFO)) as boolean;
}

export function isGroupEncryptionSystemMessage(
  message: ChatMessagePartial | undefined
) {
  return (message?.payload_type &&
    ChatMessagePayloadType.GROUP_ENCRYPTION_WELCOME <= message.payload_type &&
    message.payload_type <= ChatMessagePayloadType.GROUP_ENCRYPTION_LEAVE) as boolean;
}

export function isGroupExpireDurationSystemMessage(
  message: ChatMessagePartial | undefined
) {
  return (message?.payload_type === ChatMessagePayloadType.EXPIRE_DURATION) as boolean;
}

export function isGroupSystemMessage(message: ChatMessagePartial | undefined) {
  return (
    isDeleteMessage(message) ||
    isGroupUpdateSystemMessage(message) ||
    isGroupEncryptionSystemMessage(message) ||
    isGroupExpireDurationSystemMessage(message)
  );
}

export function isSystemMessage(message: ChatMessagePartial | undefined) {
  return isMediaMessage(message) || isGroupSystemMessage(message);
}

export function isMeMessage(message: ChatMessagePartial | undefined) {
  return (message &&
    message.category === ChatMessageCategory.SEND &&
    !isSystemMessage(message)) as boolean;
}
